import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatBRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, UserX, UserCheck, Calendar, Send, Edit3, Eraser, Loader2 } from "lucide-react";

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgDialog, setMsgDialog] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgViaWa, setMsgViaWa] = useState(false);
  const [sending, setSending] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetPreview, setResetPreview] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);

  const load = () => api.get(`/admin/users/${id}`).then((r) => setUser(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="p-10 text-stone-500">Carregando…</div>;
  if (!user) return <div className="p-10 text-stone-500">Usuário não encontrado.</div>;

  const changePlan = async (plan) => {
    try {
      await api.patch(`/admin/users/${id}/plan`, { plan });
      toast.success(`Plano alterado para ${plan}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggleSuspend = async () => {
    const newStatus = !user.is_active;
    try {
      await api.patch(`/admin/users/${id}/suspend`, { is_active: newStatus });
      toast.success(newStatus ? "Conta reativada" : "Conta suspensa");
      load();
    } catch (e) { toast.error("Erro"); }
  };

  const extendTrial = async () => {
    try {
      await api.post(`/admin/users/${id}/extend-trial`, { days: parseInt(extendDays) });
      toast.success(`Trial estendido por ${extendDays} dias`);
      load();
    } catch (e) { toast.error("Erro"); }
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/users/${id}/message`, { text: msgText, via_whatsapp: msgViaWa });
      toast.success(`Enviado: ${data.delivered.join(", ")}`);
      setMsgDialog(false); setMsgText(""); setMsgViaWa(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSending(false); }
  };

  const resetUrl = (dryRun) =>
    `/admin/reset-user-history?target_email=${encodeURIComponent(user.email)}` +
    `&confirm=${encodeURIComponent(`RESET-${user.email}`)}&dry_run=${dryRun}`;

  const openReset = async () => {
    setResetDialog(true);
    setResetPreview(null);
    setResetBusy(true);
    try {
      const { data } = await api.post(resetUrl(true));
      setResetPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao calcular o que será apagado");
      setResetDialog(false);
    } finally { setResetBusy(false); }
  };

  const confirmReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post(resetUrl(false));
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Histórico zerado: ${total} registros apagados`);
      setResetDialog(false); setResetPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao zerar histórico");
    } finally { setResetBusy(false); }
  };

  const LABELS = {
    leads: "Leads", tasks: "Tarefas", messages: "Mensagens/conversas",
    agenciamentos: "Agenciamentos", audit_reports: "Relatórios de auditoria",
    audit_improvements: "Melhorias da auditoria", coach_state: "Estado do coach",
  };

  const trialEnds = user.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const trialActive = trialEnds && trialEnds > new Date() && !user.plan_paid_at;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto space-y-5" data-testid="admin-user-detail-root">
      <button onClick={() => navigate("/admin/users")} className="text-sm text-stone-600 hover:text-[#1B3A2F] flex items-center gap-1.5">
        <ArrowLeft className="w-3.5 h-3.5" /> Voltar para usuários
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-2xl text-white ${user.is_active === false ? 'bg-stone-400' : 'bg-[#1B3A2F]'}`}>
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#1B3A2F]">{user.name}</h1>
            <div className="text-sm text-stone-500">{user.email} · {user.phone}</div>
            <div className="flex gap-2 mt-2 flex-wrap">
              {user.is_active === false && <Badge variant="destructive">Suspenso</Badge>}
              {user.role === "admin" && <Badge className="bg-[#D4651A]">admin</Badge>}
              {user.plan_paid_at ? <Badge variant="outline" className="capitalize">{user.plan} pago</Badge> : <Badge className="bg-blue-100 text-blue-700">trial</Badge>}
              {trialActive && <Badge variant="outline">Trial até {trialEnds.toLocaleDateString("pt-BR")}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="admin-msg-user-btn" onClick={() => setMsgDialog(true)} className="bg-[#1B3A2F] hover:bg-[#244A3C]">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Mandar mensagem
          </Button>
          <Button
            data-testid="admin-suspend-btn"
            onClick={toggleSuspend}
            variant="outline"
            className={user.is_active === false ? "border-green-400 text-green-700" : "border-red-300 text-red-600"}
          >
            {user.is_active === false ? <UserCheck className="w-3.5 h-3.5 mr-1.5" /> : <UserX className="w-3.5 h-3.5 mr-1.5" />}
            {user.is_active === false ? "Reativar" : "Suspender"}
          </Button>
          <Button
            data-testid="admin-reset-history-btn"
            onClick={openReset}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <Eraser className="w-3.5 h-3.5 mr-1.5" /> Zerar histórico
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Leads" value={user.leads_count} />
        <StatCard label="Imóveis" value={user.properties_count} />
        <StatCard label="Mensagens" value={user.messages_count} />
        <StatCard label="Fechamentos" value={user.closings_count} accent />
      </div>

      {/* Plan + Trial */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 rounded-2xl">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><Edit3 className="w-4 h-4" /> Mudar plano</h2>
          <Select value={user.plan} onValueChange={changePlan}>
            <SelectTrigger data-testid="admin-change-plan-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="solo">Solo (R$ 67)</SelectItem>
              <SelectItem value="dupla">Dupla (R$ 117)</SelectItem>
              <SelectItem value="equipe">Equipe (R$ 247)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-stone-500 mt-2">Ao mudar, marca como pago automaticamente.</p>
        </Card>

        <Card className="p-5 rounded-2xl">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Estender trial</h2>
          <div className="flex gap-2">
            <Input
              type="number"
              data-testid="admin-extend-days-input"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              className="w-24"
            />
            <Button data-testid="admin-extend-btn" onClick={extendTrial} className="bg-[#1B3A2F] hover:bg-[#244A3C]">
              + Estender dias
            </Button>
          </div>
          <p className="text-xs text-stone-500 mt-2">
            {trialEnds ? `Trial atual termina em ${trialEnds.toLocaleDateString("pt-BR")}` : "Sem trial ativo"}
          </p>
        </Card>
      </div>

      {/* Funnel */}
      <Card className="p-5 rounded-2xl">
        <h2 className="font-heading font-bold mb-4">Funil de vendas</h2>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {Object.entries(user.funnel || {}).map(([k, v]) => (
            <div key={k} className="text-center border border-stone-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{k}</div>
              <div className="font-heading text-2xl font-extrabold text-[#1B3A2F] mt-1">{v}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Last messages */}
      <Card className="p-5 rounded-2xl">
        <h2 className="font-heading font-bold mb-3">Últimas conversas com a IA</h2>
        {user.last_messages?.length === 0 ? (
          <p className="text-sm text-stone-500">Sem mensagens.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {user.last_messages?.map((m) => (
              <div key={m.id} className={`p-3 rounded-lg text-sm ${m.role === "user" ? "bg-[#D9FDD3] ml-12" : "bg-stone-100 mr-12"}`}>
                <div className="text-[10px] text-stone-500 uppercase tracking-wider font-semibold mb-1">
                  {m.role === "user" ? "Corretor" : m.from_admin ? "Admin" : "estatebroker.AI"} · {new Date(m.created_at).toLocaleString("pt-BR")}
                </div>
                <div className="whitespace-pre-wrap text-stone-700">{m.content}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Reset history dialog */}
      <Dialog open={resetDialog} onOpenChange={(v) => { if (!resetBusy) { setResetDialog(v); if (!v) setResetPreview(null); } }}>
        <DialogContent data-testid="admin-reset-history-dialog">
          <DialogHeader><DialogTitle>Zerar histórico de {user.name}</DialogTitle></DialogHeader>
          {resetBusy && !resetPreview ? (
            <div className="py-6 flex items-center gap-2 text-stone-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Calculando o que será apagado...
            </div>
          ) : resetPreview ? (
            <div className="space-y-3 text-sm">
              <p className="text-stone-600">
                O login e a senha continuam funcionando. Vai apagar tudo abaixo, sem volta:
              </p>
              <div className="rounded-lg border border-stone-200 divide-y" data-testid="admin-reset-preview">
                {Object.entries(resetPreview.counts || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between px-3 py-2">
                    <span className="text-stone-600">{LABELS[k] || k}</span>
                    <span className="font-semibold text-[#1B3A2F]">{v}</span>
                  </div>
                ))}
              </div>
              <div className="text-stone-700 font-semibold">
                Total: {resetPreview.total_records} registros
              </div>
              <p className="text-xs text-stone-500">
                Também limpa fila de reativação, buscas e atualizações pendentes do WhatsApp.
              </p>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(false)} disabled={resetBusy}>Cancelar</Button>
            <Button
              data-testid="admin-reset-history-confirm-btn"
              onClick={confirmReset}
              disabled={resetBusy || !resetPreview}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {resetBusy && resetPreview ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Apagar tudo agora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direct message dialog */}
      <Dialog open={msgDialog} onOpenChange={setMsgDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mandar mensagem direta para {user.name}</DialogTitle></DialogHeader>
          <Textarea
            data-testid="admin-direct-msg-input"
            rows={5}
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            placeholder="O que você quer dizer pra ele?"
          />
          <div className="flex items-center gap-2">
            <Checkbox id="via-wa" checked={msgViaWa} onCheckedChange={setMsgViaWa} data-testid="admin-msg-via-wa" />
            <Label htmlFor="via-wa" className="text-sm cursor-pointer">Enviar também pelo WhatsApp</Label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgDialog(false)}>Cancelar</Button>
            <Button data-testid="admin-send-direct-btn" onClick={sendMessage} disabled={sending} className="bg-[#1B3A2F] hover:bg-[#244A3C]">
              {sending ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <Card className={`p-4 rounded-xl ${accent ? "bg-[#1B3A2F] text-white" : "bg-white"}`}>
      <div className={`text-xs uppercase font-semibold tracking-wider ${accent ? "text-white/70" : "text-stone-500"}`}>{label}</div>
      <div className={`font-heading text-2xl font-extrabold ${accent ? "text-[#D4651A]" : "text-[#1B3A2F]"}`}>{value}</div>
    </Card>
  );
}
